/**
 * Interview Round Template Service
 *
 * Handles:
 * 1. Fetching question types and styles from backend
 * 2. Generating interview round templates via n8n workflow
 */

import apiClient from "./api.client.service";

// Types for question styles
export interface QuestionStyleOption {
  value: string;
  label: string;
  description?: string;
}

export type GroupedQuestionStyles = Record<string, QuestionStyleOption[]>;

// Types for template generation
export interface GenerateTemplateRequest {
  must_match_requirements: string;
  other_requirements?: string;
  skills?: string;
  question_type: "MCQ" | "Descriptive" | "Coding" | "Mixed";
  question_style: string;
  difficulty_level: "Beginner" | "Intermediate" | "Advanced" | "Expert";
}

export interface GenerateTemplateFromJDRequest {
  jobDescriptionId: string;
  question_type: "MCQ" | "Descriptive" | "Coding" | "Mixed";
  question_style: string;
  difficulty_level: "Beginner" | "Intermediate" | "Advanced";
}

export interface GenerateTemplateResponse {
  questionGenerationPrompt?: string;
  evaluationCriteria?: string;
  suggestedTitle?: string;
  [key: string]: any;
}

class InterviewRoundTemplateService {
  private APIBaseUrl: string;
  private static instance: InterviewRoundTemplateService;

  // Cache for question styles (5 minutes TTL)
  private questionStylesCache: {
    data: GroupedQuestionStyles | null;
    timestamp: number;
  } = {
    data: null,
    timestamp: 0,
  };
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  private constructor() {
    this.APIBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
  }

  static getInstance(): InterviewRoundTemplateService {
    if (!InterviewRoundTemplateService.instance) {
      InterviewRoundTemplateService.instance =
        new InterviewRoundTemplateService();
    }
    return InterviewRoundTemplateService.instance;
  }

  /**
   * Get all question styles grouped by question type
   *
   * @returns Object with question types as keys and array of style options as values
   *
   * @example Response:
   * {
   *   "MCQ": [
   *     { "value": "concept-recall", "label": "Concept Recall", "description": "..." },
   *     { "value": "scenario-based-mcq", "label": "Scenario-Based MCQ", "description": "..." }
   *   ],
   *   "Descriptive": [...],
   *   "Coding": [...],
   *   "Mixed": [...]
   * }
   */
  async getQuestionStyles(
    forceRefresh: boolean = false
  ): Promise<GroupedQuestionStyles> {
    // Check cache first
    if (
      !forceRefresh &&
      this.questionStylesCache.data &&
      Date.now() - this.questionStylesCache.timestamp < this.CACHE_TTL
    ) {
      return this.questionStylesCache.data;
    }

    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/interview/question-styles`
      );

      const styles = response.data as GroupedQuestionStyles;

      // Update cache
      this.questionStylesCache = {
        data: styles,
        timestamp: Date.now(),
      };

      return styles;
    } catch (error) {
      console.error(
        "🚀 ~ InterviewRoundTemplateService ~ getQuestionStyles ~ error:",
        error
      );

      // Return stale cache if available
      if (this.questionStylesCache.data) {
        console.warn("Using stale question styles cache due to API error");
        return this.questionStylesCache.data;
      }

      // Return empty object if no cache
      return {};
    }
  }

  /**
   * Get available question types
   *
   * @returns Array of question type strings
   */
  async getQuestionTypes(): Promise<string[]> {
    const styles = await this.getQuestionStyles();
    return Object.keys(styles);
  }

  /**
   * Get styles for a specific question type
   *
   * @param questionType - The type to filter by (e.g., "MCQ", "Mixed")
   * @returns Array of style options for that type
   */
  async getStylesForType(questionType: string): Promise<QuestionStyleOption[]> {
    const styles = await this.getQuestionStyles();
    return styles[questionType] || [];
  }

  /**
   * Generate interview round template using n8n workflow
   *
   * @param request - Template generation parameters
   * @param signal - Optional AbortSignal for cancellation
   * @returns Generated template with prompt and evaluation criteria
   */
  async generateTemplate(
    request: GenerateTemplateRequest,
    signal?: AbortSignal
  ): Promise<GenerateTemplateResponse> {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/admin/job-description/generate/interview-round-template`,
        request,
        { signal } // Pass abort signal to axios
      );
      // Backend wraps response in { success: true, data: template }
      const result = response.data;
      console.log("🚀 ~ generateTemplate ~ axios response.data:", JSON.stringify(result, null, 2));
      
      // Unwrap the backend wrapper
      let template = result?.data || result;
      
      // If n8n returns an array, take the first item
      if (Array.isArray(template)) {
        console.log("🚀 ~ generateTemplate ~ response is array, taking first item");
        template = template[0];
      }
      
      // If there's a nested output or result
      template = template?.output || template?.result || template?.response || template;
      
      console.log("🚀 ~ generateTemplate ~ final template:", JSON.stringify(template, null, 2));
      console.log("🚀 ~ generateTemplate ~ template keys:", Object.keys(template || {}));
      
      return template;
    } catch (error: any) {
      // Don't log or throw for cancelled requests
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        console.log("🚀 ~ generateTemplate ~ request cancelled");
        throw error; // Re-throw so caller knows it was cancelled
      }
      
      console.error(
        "🚀 ~ InterviewRoundTemplateService ~ generateTemplate ~ error:",
        error
      );
      throw new Error(
        error?.response?.data?.message ||
          "Failed to generate template. Please try again."
      );
    }
  }

  /**
   * Generate interview round template from a job description
   *
   * This extracts requirements and skills from the job description
   * and uses them to generate a customized template
   *
   * @param request - Job description ID and template parameters
   * @returns Generated template with prompt and evaluation criteria
   */
  async generateTemplateFromJobDescription(
    request: GenerateTemplateFromJDRequest
  ): Promise<GenerateTemplateResponse> {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/admin/job-description/generate/interview-round-from-jd`,
        request
      );
      // Backend wraps response in { success: true, data: template }
      const result = response.data;
      console.log("🚀 ~ InterviewRoundTemplateService ~ generateTemplateFromJD ~ raw response:", result);
      return result?.data || result;
    } catch (error: any) {
      console.error(
        "🚀 ~ InterviewRoundTemplateService ~ generateTemplateFromJD ~ error:",
        error
      );
      throw new Error(
        error?.response?.data?.message ||
          "Failed to generate template from job description. Please try again."
      );
    }
  }

  /**
   * Invalidate question styles cache
   */
  invalidateCache(): void {
    this.questionStylesCache = {
      data: null,
      timestamp: 0,
    };
  }
}

const interviewRoundTemplateService = InterviewRoundTemplateService.getInstance();
export default interviewRoundTemplateService;

